import { z } from 'zod';
import { cleanString } from './clean.js';
import { CompanyType } from './onboarding-enums.js';

// CSV-backfill of contractor records (structured fields only, no documents).
// Mirrors the field rules used by the onboarding flow. CSV values arrive as
// strings, so every field is parsed from a string. Headers must match these
// keys (see BACKFILL_COLUMNS for the template/order).

const optionalRegex = (re: RegExp, msg: string) =>
  z
    .string()
    .optional()
    .default('')
    .refine((v) => v === '' || re.test(v), msg);
// Free-text fields are cleanString()-normalized (T-200) — the same rules as the
// live onboarding schema, so both intake paths store identical bytes.
const optionalStr = (max: number) => cleanString(z.string().max(max)).optional().default('');
const OptionalMoney = z
  .string()
  .optional()
  .default('')
  .refine((v) => v === '' || (Number.isFinite(Number(v)) && Number(v) > 0), {
    message: 'Must be a positive number or blank',
  });
const OptionalEmail = z
  .string()
  .optional()
  .default('')
  .refine((v) => v === '' || z.string().email().safeParse(v).success, { message: 'Invalid email' });

// Minimal (stub) backfill (T-049): only five fields are required —
// resource_name, business_name, client_name, product_service, worker_id. Every
// other field may be blank and is filled in later. The live onboarding flow is
// unchanged (it keeps its own full required-field schema).
export const BackfillRow = z.object({
  resource_name: cleanString(z.string().min(1).max(200)),
  resource_email: OptionalEmail,
  // Blank → defaults to the configured uIT signer email at import time (else null).
  uit_contact_email: OptionalEmail,
  role: optionalStr(200),
  client_name: cleanString(z.string().min(1)),
  product_service: cleanString(z.string().min(1).max(400)),
  rate_to_contractor: OptionalMoney,
  rate_to_client: OptionalMoney,
  start_date: optionalRegex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be YYYY-MM-DD or blank'),
  end_date: optionalRegex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be YYYY-MM-DD or blank'),
  business_name: cleanString(z.string().min(1).max(200)),
  business_email: OptionalEmail,
  mobile_phone: optionalRegex(/^\d{3}-\d{3}-\d{4}$/, 'Phone must be xxx-xxx-xxxx or blank'),
  office_phone: optionalRegex(/^\d{3}-\d{3}-\d{4}$/, 'Phone must be xxx-xxx-xxxx or blank'),
  // Blank → defaults to business_address_state at import time (else null).
  state_of_incorporation: optionalStr(60),
  // Accept LLC / C-Corp / S-Corp (and underscore forms), normalize; blank allowed.
  company_type: z
    .string()
    .optional()
    .default('')
    .transform((s) => s.trim().toUpperCase().replace('-', '_'))
    .pipe(z.literal('').or(CompanyType)),
  business_address_street: optionalStr(200),
  business_address_city: optionalStr(120),
  business_address_state: optionalStr(60),
  business_address_zip: optionalStr(20),
  resource_mailing_address_street: optionalStr(200),
  resource_mailing_address_city: optionalStr(120),
  resource_mailing_address_state: optionalStr(60),
  resource_mailing_address_zip: optionalStr(20),
  ach_routing: optionalRegex(/^\d{9}$/, 'ACH routing must be 9 digits or blank'),
  ach_account: optionalRegex(/^\d{5,17}$/, 'ACH account must be 5–17 digits or blank'),
  ein: optionalRegex(/^\d{2}-\d{7}$/, 'EIN must be xx-xxxxxxx or blank'),
  worker_id: z.string().min(1, 'Worker ID is required'),
});
export type BackfillRow = z.infer<typeof BackfillRow>;

// Canonical column order — used to render the downloadable CSV template.
export const BACKFILL_COLUMNS = [
  'resource_name',
  'resource_email',
  'uit_contact_email',
  'role',
  'client_name',
  'product_service',
  'rate_to_contractor',
  'rate_to_client',
  'start_date',
  'end_date',
  'business_name',
  'business_email',
  'mobile_phone',
  'office_phone',
  'state_of_incorporation',
  'company_type',
  'business_address_street',
  'business_address_city',
  'business_address_state',
  'business_address_zip',
  'resource_mailing_address_street',
  'resource_mailing_address_city',
  'resource_mailing_address_state',
  'resource_mailing_address_zip',
  'ach_routing',
  'ach_account',
  'ein',
  'worker_id',
] as const;

export interface BackfillInvalidRow {
  row_number: number;
  errors: string[];
}

export const BackfillPreviewResponse = z.object({
  rows_in: z.number().int().nonnegative(),
  rows_valid: z.number().int().nonnegative(),
  rows_invalid: z.number().int().nonnegative(),
  invalid: z.array(z.object({ row_number: z.number().int(), errors: z.array(z.string()) })),
});
export type BackfillPreviewResponse = z.infer<typeof BackfillPreviewResponse>;

export const BackfillImportResponse = z.object({
  imported: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
});
export type BackfillImportResponse = z.infer<typeof BackfillImportResponse>;
